import { ContentDetailTabType } from '@diritto/common/content/constants';
import { EpisodeOrderType } from '@diritto/common/episode/constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ReplyOrder } from '@diritto/common/reply/constants';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { isPC } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useRouter } from 'next/navigation';
import { EpisodeOrderTypeMenu } from 'app-components/contents/detail/EpisodeOrderTypeMenu';
import { ReplyOrderTypeMenu } from 'app-components/contents/detail/ReplyOrderTypeMenu';
import { useGetContentFancySharesInfiniteQuery } from 'queries/fancyShare/userGetContentFancySharesInfiniteQuery';
import { useCallback } from 'react';
import { Divider } from 'ui-components/common/Divider';
import { Link } from 'ui-components/common/Link';
import { useGetContentRollingsInfiniteQuery } from 'queries/relay/useGetContentRollingsInfiniteQuery';
import { numberComma, shortenCount } from '@diritto/common/utils/string';

interface ContentDetailTabProps {
  className?: string;
  content: ContentPublicRelayDto;
  episodeOrderType: EpisodeOrderType;
  replyOrderType: ReplyOrder;
  tabType: ContentDetailTabType;
}

export const ContentDetailTab = ({
  content,
  className,
  tabType,
  episodeOrderType,
  replyOrderType,
}: ContentDetailTabProps) => {
  const router = useRouter();
  const { includeAdult, isLoaded } = useAuthState();

  const { tracker } = useTrackingState();

  const handleClickEpisodeOrderType = useCallback(
    (type: EpisodeOrderType) => {
      tracker.track(TrackingEventType.ClickContentDetailEpisodeOrderTab, { contentId: content.id, tabType: type });
      router.replace(`/contents/${content.id}?tabType=${tabType}&episodeOrderType=${type}`);
    },
    [tracker, content.id, router, tabType],
  );

  const handleClickReplyOrderType = useCallback(
    (type: ReplyOrder) => {
      tracker.track(TrackingEventType.ClickContentDetailReplyOrderTab, { contentId: content.id, tabType: type });
      router.replace(`/contents/${content.id}?tabType=${tabType}&replyOrderType=${type}`);
    },
    [content.id, router, tabType, tracker],
  );

  const { data: fancyShareData } = useGetContentFancySharesInfiniteQuery(content.id, includeAdult);
  const fancyShareTotalCount = isLoaded ? (fancyShareData?.pages[0]?.totalCount ?? 0) : 0;

  const { data: rollingData } = useGetContentRollingsInfiniteQuery(content.id, includeAdult);
  const rollingTotalCount = rollingData?.pages[0]?.totalCount ?? 0;

  return (
    <StyledWrapper className={className}>
      <StyledListWrapper>
        <Link
          href={`/contents/${content.id}?tabType=${ContentDetailTabType.Episode}&episodeOrderType=${episodeOrderType}`}
          replace
          shallow>
          <StyledListItem isActive={tabType === ContentDetailTabType.Episode}>
            회차&nbsp;{numberComma(content.counts.episodeCount)}
          </StyledListItem>
        </Link>
        <Link
          href={`/contents/${content.id}?tabType=${ContentDetailTabType.Reply}&replyOrderType=${replyOrderType}`}
          replace
          shallow>
          <StyledListItem isActive={tabType === ContentDetailTabType.Reply}>
            댓글&nbsp;{shortenCount(content.counts.replyCount)}
          </StyledListItem>
        </Link>
        {content.isFancyShareEnabled && (
          <Link href={`/contents/${content.id}?tabType=${ContentDetailTabType.FancyShare}`} replace shallow>
            <StyledListItem isActive={tabType === ContentDetailTabType.FancyShare}>
              이멋공&nbsp;{shortenCount(fancyShareTotalCount)}
            </StyledListItem>
          </Link>
        )}
        <Link href={`/contents/${content.id}?tabType=${ContentDetailTabType.Rolling}`} replace shallow>
          <StyledListItem isActive={tabType === ContentDetailTabType.Rolling}>
            롤링&nbsp;{numberComma(rollingTotalCount)}
          </StyledListItem>
        </Link>
        {tabType === ContentDetailTabType.Episode && (
          <EpisodeOrderTypeMenu orderType={episodeOrderType} onChangeOrderType={handleClickEpisodeOrderType} />
        )}
        {tabType === ContentDetailTabType.Reply && (
          <ReplyOrderTypeMenu orderType={replyOrderType} onChangeOrderType={handleClickReplyOrderType} />
        )}
      </StyledListWrapper>
      <StyledDivider color={Color.Gray200} />
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  flex-direction: column;
  margin-top: 16px;
`;

const StyledListWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  font-weight: ${FontWeight.Regular};
`;

const StyledListItem = styled.div(
  ({ isActive = false }: { isActive?: boolean }) => css`
    height: 100%;
    margin-right: 14px;
    padding: 16px 0;
    display: flex;
    justify-content: center;
    flex-direction: column;
    cursor: pointer;
    text-align: center;
    color: ${Color.Black};
    ${Typography.XXSmall}

    ${isActive &&
    css`
      font-weight: ${FontWeight.Bold};
      padding: 16px 0 13px 0;
      border-bottom: 3px solid ${Color.Black};
    `}
  `,
);

const StyledDivider = styled(Divider)`
  width: 100%;
  bottom: 0;

  @media (${isPC}) {
    left: 0;
  }
`;
